// pages/batches/View.tsx
import { useBatchByIdQuery, useUpdateBatchStatusMutation, useAdvanceBatchSemesterMutation, useUpdateBatchSemesterMutation } from "@/hooks/api/batch.queries";
import { useStudentsByBatchQuery } from "@/hooks/api/student.queries";
import { useFormDialog } from "@/hooks/useDialogState";
import StudentDialog from "@/components/formDialog/StudentDialog";
import BulkUploadStudentsDialog from "@/components/formDialog/BulkUploadStudentsDialog";
import { Spinner, ErrorMessage, PageContainer, CustomAlertDialog } from "@/components";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BatchStatus } from "@/services/batch.service";
import { GraduationCap, Users, Plus, ArrowLeft, User, Upload } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { useState } from "react";
import SetSemesterDialog from "@/components/formDialog/SetSemesterDialog";
import { notifyError } from "@/lib/notification";

const BatchView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    data: batch,
    isLoading,
    isError,
    refetch,
  } = useBatchByIdQuery(id || "");

  const studentDialog = useFormDialog({ batchId: "" });
  const statusMutation = useUpdateBatchStatusMutation(id || "");
  const advanceMutation = useAdvanceBatchSemesterMutation(id || "");
  const semesterMutation = useUpdateBatchSemesterMutation(id || "");
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const {
    data: studentsData,
    isLoading: isLoadingStudents,
  } = useStudentsByBatchQuery({ batch_id: id || "", eligible_only: true, limit: 50 });
  const [showAdvanceConfirm, setShowAdvanceConfirm] = useState(false);
  const [showSetSemesterDialog, setShowSetSemesterDialog] = useState(false);

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  if (isError || !batch) {
    return (
      <ErrorMessage
        message="Failed to load batch details."
        onRetry={() => refetch()}
      />
    );
  }

  const currentYear = new Date().getFullYear();
  const maxSemester = (batch.end_year - batch.start_year) * 2;

  const handleStatusChange = (val: string) => {
    if (val === "completed" && currentYear < batch.end_year) {
      notifyError(`Cannot mark as completed before the end year (${batch.end_year}).`);
      return;
    }
    
    if (val === "ongoing" && batch.status === "completed" && currentYear >= batch.end_year) {
      notifyError(`Cannot change a completed batch back to ongoing after its end year.`);
      return;
    }

    statusMutation.mutate(val as BatchStatus);
  };

  return (
    <PageContainer className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/batches")}
            className="flex shrink-0 items-center gap-2 text-gray-500"
          >
            <ArrowLeft size={16} />
            Back
          </Button>
          <div>
            <h1 className="text-xl font-bold sm:text-2xl">{batch.name}</h1>
            <p className="text-xs text-gray-500 sm:text-sm">
              {batch.start_year} – {batch.end_year}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Status Selector */}
          <Select
            value={batch.status}
            onValueChange={handleStatusChange}
            disabled={statusMutation.isPending}
          >
            <SelectTrigger className="w-full sm:w-36 gap-2 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ongoing">Ongoing</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            size="sm"
            className="flex flex-1 items-center justify-center gap-2 sm:flex-none"
            onClick={() => setShowAdvanceConfirm(true)}
            disabled={advanceMutation.isPending || batch.current_semester >= maxSemester}
          >
            <GraduationCap size={14} />
            {advanceMutation.isPending ? "Advancing..." : "Advance"}
          </Button>

          {/* Set Semester */}
          <Button
            variant="outline"
            size="sm"
            className="flex-1 sm:flex-none"
            onClick={() => setShowSetSemesterDialog(true)}
            disabled={semesterMutation.isPending}
          >
            Set Sem
          </Button>

          <Button
            className="flex items-center justify-center gap-2 bg-gray-900 text-white hover:bg-gray-800 w-full sm:w-auto"
            size="sm"
            onClick={() => studentDialog.open({ batchId: id! })}
          >
            <Plus size={14} />
            Add Student
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Card className="border-gray-100 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-blue-50 p-2 text-blue-600">
                <Users size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold">{batch.current_semester}</p>
                <p className="text-xs text-gray-400">Current Semester</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-gray-100 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-green-50 p-2 text-green-600">
                <Users size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold capitalize">
                  {batch.is_active ? "Active" : "Inactive"}
                </p>
                <p className="text-xs text-gray-400">Batch Status</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-gray-100 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-purple-50 p-2 text-purple-600">
                <Users size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold">{batch.start_year}</p>
                <p className="text-xs text-gray-400">Start Year</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-gray-100 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-orange-50 p-2 text-orange-600">
                <Users size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold">{batch.end_year}</p>
                <p className="text-xs text-gray-400">End Year</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Students List */}
      <Card className="border-gray-100 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base font-semibold">
            Students
            {studentsData && (
              <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
                {studentsData.metaData.pagination.total}
              </span>
            )}
          </CardTitle>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setBulkUploadOpen(true)}
            >
              <Upload size={13} className="mr-1" />
              Bulk Upload
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => studentDialog.open({ batchId: id! })}
            >
              <Plus size={13} className="mr-1" />
              Add Student
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoadingStudents ? (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          ) : !studentsData?.students.length ? (
            <div className="flex flex-col items-center gap-2 py-10 text-center text-sm text-gray-400">
              <User size={28} className="text-gray-300" />
              <p>No students found in this batch.</p>
              <Button
                size="sm"
                variant="outline"
                className="mt-1"
                onClick={() => studentDialog.open({ batchId: id! })}
              >
                <Plus size={13} className="mr-1" />
                Add First Student
              </Button>
            </div>
          ) : (
            <ul className="divide-y divide-gray-50">
              {studentsData.students.map((student) => (
                <li
                  key={student.id}
                  onClick={() => navigate(`/students/${student.id}`)}
                  className="flex cursor-pointer flex-col gap-1 py-3 transition-colors hover:bg-gray-50 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-50 text-xs font-semibold text-blue-600">
                      {student.full_name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">{student.full_name}</p>
                      <p className="text-xs text-gray-400">
                        {student.roll_number}
                        {student.department && ` · ${student.department}`}
                        {student.year_of_study && ` · Year ${student.year_of_study}`}
                      </p>
                      <p className="text-xs text-gray-400 break-all">{student.org_email}</p>
                    </div>
                  </div>
                  <Badge
                    variant={student.is_active ? "secondary" : "destructive"}
                    className="w-fit rounded-full px-2.5 text-xs font-medium"
                  >
                    {student.is_active ? "Active" : "Inactive"}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Add Student Dialog */}
      <StudentDialog
        isOpen={studentDialog.state.isOpen}
        onOpenChange={(open) => !open && studentDialog.close()}
        onCancel={studentDialog.close}
        batchId={studentDialog.state.batchId}
      />

      {/* Advance Semester Confirmation */}
      <CustomAlertDialog
        isOpen={showAdvanceConfirm}
        onOpenChange={(open) => !open && setShowAdvanceConfirm(false)}
        title="Advance Semester?"
        description={`This will move ${batch.name} from Semester ${batch.current_semester} to Semester ${batch.current_semester + 1}. This cannot be undone.`}
        confirmText="Yes, Advance"
        cancelText="Cancel"
        onConfirm={() => {
          advanceMutation.mutate(undefined, {
            onSuccess: () => setShowAdvanceConfirm(false),
            onError: () => setShowAdvanceConfirm(false),
          });
        }}
        onCancel={() => setShowAdvanceConfirm(false)}
      />
      {/* Set Semester Dialog */}
      <SetSemesterDialog
        isOpen={showSetSemesterDialog}
        onOpenChange={(open) => !open && setShowSetSemesterDialog(false)}
        currentSemester={batch.current_semester}
        maxSemester={maxSemester}
        batchName={batch.name}
        isPending={semesterMutation.isPending}
        onConfirm={(sem) =>
          semesterMutation.mutate(sem, {
            onSuccess: () => setShowSetSemesterDialog(false),
          })
        }
        onCancel={() => setShowSetSemesterDialog(false)}
      />
      {/* Bulk Upload Dialog */}
      <BulkUploadStudentsDialog
        isOpen={bulkUploadOpen}
        onClose={() => setBulkUploadOpen(false)}
        batchId={id!}
      />
    </PageContainer>
  );
};

export default BatchView;
